import React, { useCallback, useEffect, useState } from 'react';
import './styles/App.css';
import Header from './layouts/Header';
import Footer from './layouts/Footer';
import HomeNew from './pages/HomeNew';
import { Features, SystemFeatures, Workflow, Knowledge } from './components/features';
import LoginModal from './components/modals/LoginModal/LoginModal';
import RegisterModal from './components/modals/RegisterModal';
import TemplateSelector from './components/modals/TemplateSelector';
import { ToastProvider } from './components/common/Toast';
import { LoadingProvider } from './components/common/Loading';
import { fetchCurrentUser, logout as logoutRequest } from './api/auth';
import { getStoredToken } from './api/client';
import LabelCreator from './pages/LabelCreator';
import Profile from './pages/Profile';
import LabelViewTest from './pages/LabelViewTest';
import LabelView from './pages/LabelView';
import GDSTraceabilityViewTest from './pages/GDSTraceabilityViewTest';
import GdsThemePreview from './pages/GdsThemePreview';
import UiCheckPreview from './pages/UiCheckPreview';

function App() {
  const [showLogin, setShowLogin] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showLabelCreator, setShowLabelCreator] = useState(false);
  const [showTemplateSelector, setShowTemplateSelector] = useState(false);
  const [initialTemplates, setInitialTemplates] = useState([]);
  const [templateVariant, setTemplateVariant] = useState('default'); // 模板变体
  const [editingLabelData, setEditingLabelData] = useState(null); // 编辑的标签数据
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState(null);
  const [isLoadingUser, setIsLoadingUser] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  
  // 检查是否为测试模式
  const isTestMode = window.location.search.includes('test=view');
  const isGDSTestMode = window.location.search.includes('test=gds');
  const isGdsThemePreview = window.location.search.includes('preview=gds-themes');
  const isUiCheckPreview = window.location.search.includes('preview=ui-check');
  const path = window.location.pathname || '';

  const refreshUser = useCallback(async () => {
    try {
      setIsLoadingUser(true);
      const currentUser = await fetchCurrentUser();
      setUser(currentUser);
      setIsLoggedIn(true);
    } catch (error) {
      console.error('Failed to load user info:', error);
      setUser(null);
      setIsLoggedIn(false);
    } finally {
      setIsLoadingUser(false);
      setIsInitializing(false);
    }
  }, []);

  useEffect(() => {
    if (getStoredToken()) {
      refreshUser();
    } else {
      setIsInitializing(false);
    }
  }, [refreshUser]);

  const handleOpenLogin = () => {
    setShowRegister(false);
    setShowLogin(true);
  };

  const handleOpenRegister = () => {
    setShowLogin(false);
    setShowRegister(true);
  };

  const handleCloseModals = () => {
    setShowLogin(false);
    setShowRegister(false);
  };

  const handleLoginSuccess = (currentUser) => {
    setUser(currentUser);
    setIsLoggedIn(true);
    setShowLogin(false);
    setShowRegister(false);
    setShowProfile(true);
  };

  const handleOpenProfile = () => {
    if (isLoggedIn) {
      setShowProfile(true);
      setShowLabelCreator(false);
      if (!user && !isLoadingUser) {
        refreshUser();
      }
    } else {
      handleOpenLogin();
    }
  };

  const handleCloseProfile = () => {
    setShowProfile(false);
  };

  const handleLogout = async () => {
    try {
      await logoutRequest();
    } catch (error) {
      console.error('Failed to logout:', error);
    } finally {
      setUser(null);
      setIsLoggedIn(false);
      setShowProfile(false);
      setShowLabelCreator(false);
    }
  };

  const handleOpenLabelCreator = () => {
    if (isLoggedIn) {
      // 显示模板选择器
      setShowTemplateSelector(true);
    } else {
      handleOpenLogin();
    }
  };

  const handleEditLabel = (labelData) => {
    // 设置编辑数据并打开标签创建器
    setEditingLabelData(labelData);
    setShowLabelCreator(true);
    setShowProfile(false);
    // 清空模板选择，因为编辑时不需要选择模板
    setInitialTemplates([]);
    setTemplateVariant('default');
  };

  const handleCloseLabelCreator = () => {
    setShowLabelCreator(false);
    setEditingLabelData(null); // 清空编辑数据
    setInitialTemplates([]);
    setTemplateVariant('default');
  };

  const handleTemplateSelect = (selection) => {
    console.log('📥 [App] handleTemplateSelect 被调用');
    console.log('📦 [App] 接收到的选择结果:', selection);
    console.log('🔍 [App] 选择结果类型:', Array.isArray(selection) ? '数组' : typeof selection);
    
    // 兼容新旧格式：如果传入的是数组，转换为新格式
    let components = [];
    let variant = 'default';
    
    if (Array.isArray(selection)) {
      // 旧格式：直接是组件数组
      console.log('📋 [App] 检测到旧格式（数组），直接使用');
      components = selection;
    } else if (selection && typeof selection === 'object') {
      // 新格式：对象包含components和variant
      console.log('📋 [App] 检测到新格式（对象），提取components和variant');
      components = selection.components || [];
      variant = selection.variant || 'default';
      console.log('📝 [App] 提取的components:', components);
      console.log('🎨 [App] 提取的variant:', variant);
    } else {
      console.warn('⚠️ [App] 未知的选择结果格式:', selection);
    }
    
    console.log('💾 [App] 准备保存状态:');
    console.log('  - components:', components);
    console.log('  - variant:', variant);
    
    // 保存选中的模板
    setInitialTemplates(components);
    setTemplateVariant(variant);
    console.log('✅ [App] 状态已更新: setInitialTemplates, setTemplateVariant');
    
    // 关闭模板选择器
    setShowTemplateSelector(false);
    console.log('❌ [App] 关闭模板选择器');
    
    // 打开标签创建器
    setShowLabelCreator(true);
    setShowProfile(false);
    console.log('✅ [App] 打开标签创建器，关闭个人中心');
    console.log('🎉 [App] 流程完成，即将渲染LabelCreator组件');
  };

  const handleCloseTemplateSelector = () => {
    setShowTemplateSelector(false);
  };

  const handleRegisterSuccess = () => {
    setShowRegister(false);
    setShowLogin(true);
  };

  const renderContent = () => {
    // GDS 配色模板本地验收页（?preview=gds-themes），不影响任何正式页面
    if (isGdsThemePreview) {
      return (
        <div className="App">
          <GdsThemePreview />
        </div>
      );
    }

    // 三项改动本地验收页（?preview=ui-check）
    if (isUiCheckPreview) {
      return (
        <div className="App">
          <UiCheckPreview />
        </div>
      );
    }

    // 分享查看路由：/view/:labelId（公开访问，适配移动端）
    if (path.startsWith('/view/')) {
      return (
        <div className="App">
          <LabelView />
        </div>
      );
    }

    // GDS追溯模板测试模式
    if (isGDSTestMode) {
      return (
        <div className="App">
          <GDSTraceabilityViewTest />
        </div>
      );
    }


    // 测试模式：显示标签查看测试页面
    if (isTestMode) {
      return (
        <div className="App">
          <LabelViewTest />
        </div>
      );
    }

    if (showLabelCreator) {
      return (
        <div className="App">
          <LabelCreator 
            onClose={handleCloseLabelCreator} 
            username={user?.username || 'xiaoleixiao'}
            initialTemplates={initialTemplates}
            initialTemplateVariant={templateVariant}
            initialLabelData={editingLabelData}
          />
        </div>
      );
    }

    if (showProfile) {
      return (
        <div className="App">
          <Header 
            onLoginClick={handleOpenProfile}
            isLoggedIn={isLoggedIn}
            onHomeClick={handleCloseProfile}
          />
          <Profile 
            user={user}
            isLoading={isInitializing || isLoadingUser}
            onClose={handleCloseProfile} 
            onLogout={handleLogout}
            onCreateLabel={handleOpenLabelCreator}
            onEditLabel={handleEditLabel}
            onProfileUpdated={setUser}
            onRefreshUser={refreshUser}
          />
          <Footer />
        </div>
      );
    }

    if (isInitializing) {
      return null;
    }

    return (
      <div className="App">
        <HomeNew
          onLoginClick={isLoggedIn ? handleOpenProfile : handleOpenLogin}
          onGetStarted={isLoggedIn ? handleOpenLabelCreator : handleOpenLogin}
        />
      </div>
    );
  };

  return (
    <ToastProvider>
      <LoadingProvider>
        {renderContent()}
        
        {/* 全局弹窗组件 - 在所有场景下都渲染 */}
        <LoginModal 
          isOpen={showLogin} 
          onClose={handleCloseModals}
          onSwitchToRegister={handleOpenRegister}
          onLoginSuccess={handleLoginSuccess}
        />
        
        <RegisterModal 
          isOpen={showRegister} 
          onClose={handleCloseModals}
          onSwitchToLogin={handleOpenLogin}
          onRegisterSuccess={handleRegisterSuccess}
        />
        
        <TemplateSelector 
          isOpen={showTemplateSelector}
          onClose={handleCloseTemplateSelector}
          onSelect={handleTemplateSelect}
        />
      </LoadingProvider>
    </ToastProvider>
  );
}

export default App;
